import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Seo } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { BRAND, BLOG_POSTS } from "@/data/site";
import { breadcrumbSchema, orgSchema } from "@/lib/schema";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { stripMarkdown } from "@/components/RichText";
import { supabase } from "@/integrations/supabase/client";
import flooringCover from "@/assets/svc-hardwood.jpg";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem } from "@/components/ui/pagination";
import { blogPageNumbers, paginateBlogPosts } from "@/lib/blog-pagination";

type Card = { slug: string; title: string; excerpt: string; category: string; readTime: string; date?: string; heroUrl?: string | null; heroAlt?: string | null };

const Blog = () => {
  const [page, setPage] = useState(1);
  const headingRef = useRef<HTMLDivElement>(null);
  const [posts, setPosts] = useState<Card[]>(
    BLOG_POSTS.map((p) => ({ slug: p.slug, title: p.title, excerpt: p.excerpt, category: p.category, readTime: p.readTime, date: p.date })),
  );
  const { posts: visiblePosts, currentPage, totalPages } = paginateBlogPosts(posts, page);
  const changePage = (nextPage: number) => {
    setPage(nextPage);
    headingRef.current?.scrollIntoView({ behavior: "instant", block: "start" });
  };

  useEffect(() => {
    supabase
      .from("blog_posts")
      .select("slug,title,excerpt,category,read_time,published_at,hero_image_url,hero_image_alt")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .then(({ data }) => {
        if (!data) return;
        const db: Card[] = data.map((p: any) => ({
          slug: p.slug, title: p.title, excerpt: p.excerpt, category: p.category, readTime: p.read_time, date: p.published_at,
          heroUrl: p.hero_image_url, heroAlt: p.hero_image_alt,
        }));
        // Merge: DB posts first, then static fallbacks not duplicated by slug
        const seen = new Set(db.map((x) => x.slug));
        const merged = [...db, ...BLOG_POSTS.filter((s) => !seen.has(s.slug)).map((p) => ({
          slug: p.slug, title: p.title, excerpt: p.excerpt, category: p.category, readTime: p.readTime, date: p.date,
        }))];
        setPosts(merged);
      });
  }, []);

  return (
    <Layout>
      <Seo
        title="Flooring Blog | Tips, Costs & Guides for Connecticut Homeowners"
        description="Practical guides on flooring costs, hardwood vs. vinyl, laminate care and choosing a flooring contractor in Danbury and Connecticut."
        jsonLd={[orgSchema(), breadcrumbSchema([{ name: "Home", url: BRAND.website }, { name: "Blog", url: `${BRAND.website}/blog` }])]}
      />
      <section className="container-page py-12 md:py-16">
        <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Blog" }]} />
        <div ref={headingRef} className="mt-6 max-w-3xl mb-10 scroll-mt-28">
          <p className="eyebrow mb-4 text-accentInk">Insights</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">Flooring guides for Connecticut homeowners.</h1>
          <p className="text-muted-foreground text-lg">Honest, no-fluff advice on costs, timelines, materials and choosing the right contractor.</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visiblePosts.map((p, index) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="card-elev group flex min-w-0 flex-col overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transform-none">
              <div className="aspect-[8/5] overflow-hidden bg-muted shrink-0">
                <img
                  src={p.heroUrl || flooringCover}
                  alt={p.heroAlt || `Hardwood flooring by VRP Flooring LLC`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transform-none motion-reduce:transition-none"
                  width={800}
                  height={500}
                  loading={index < 3 ? "eager" : "lazy"}
                  decoding="async"
                  onError={(event) => {
                    if (event.currentTarget.getAttribute("src") !== flooringCover) event.currentTarget.src = flooringCover;
                  }}
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase text-accentInk mb-3">
                <span>{p.category}</span>
                <span className="text-muted-foreground">· {p.readTime}</span>
              </div>
              <h2 className="font-display text-2xl font-semibold mb-3 leading-snug break-words tracking-normal group-hover:text-accentInk transition-colors">{stripMarkdown(p.title)}</h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-5 line-clamp-4">{stripMarkdown(p.excerpt)}</p>
              <span className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-accentInk">
                Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 motion-reduce:transform-none" />
              </span>
              </div>
            </Link>
          ))}
        </div>
        {totalPages > 1 && (
          <Pagination className="mt-10" aria-label="Blog pages">
            <PaginationContent>
              <PaginationItem>
                <Button variant="ghost" size="icon" aria-label="Previous page" disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)}>
                  <ChevronLeft />
                </Button>
              </PaginationItem>
              {blogPageNumbers(currentPage, totalPages).map((item, index) => (
                <PaginationItem key={`${item}-${index}`}>
                  {item === "ellipsis" ? <PaginationEllipsis /> : (
                    <Button variant={item === currentPage ? "default" : "ghost"} size="icon" aria-label={`Page ${item}`} aria-current={item === currentPage ? "page" : undefined} onClick={() => changePage(item)}>
                      {item}
                    </Button>
                  )}
                </PaginationItem>
              ))}
              <PaginationItem>
                <Button variant="ghost" size="icon" aria-label="Next page" disabled={currentPage === totalPages} onClick={() => changePage(currentPage + 1)}>
                  <ChevronRight />
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </section>
    </Layout>
  );
};
export default Blog;
